import React, { useState, useEffect } from "react";
import "./CulinaryLoadingModal.css";

const stages = [
  {
    badge: "Stage 1: Cleaning / Washing",
    icon: "💧",
    title: "Rinsing Fresh Produce",
    detail: "Running crisp garden vegetables under cold water to prep for clean cooking...",
    color: "#38bdf8",
    tip: "💡 Tip: Patting greens dry ensures maximum crispness when cooked!",
    pct: 16,
  },
  {
    badge: "Stage 2: Cutting / Chopping",
    icon: "🔪",
    title: "Precision Knife Slicing",
    detail: "Rapidly dicing aromatics, bell peppers, and herbs evenly on the board...",
    color: "#eab308",
    tip: "💡 Tip: A sharp knife preserves the natural juices and essential cell nutrients!",
    pct: 33,
  },
  {
    badge: "Stage 3: Fire / Heating",
    icon: "🔥",
    title: "Igniting the Burner Flame",
    detail: "Warming the stovetop burner with flaming embers for the ideal cooking heat...",
    color: "#ef4444",
    tip: "💡 Tip: Heat your skillet first before adding oil to prevent food sticking!",
    pct: 50,
  },
  {
    badge: "Stage 4: Frying / Tossing",
    icon: "🍳",
    title: "High-Heat Wok Searing",
    detail: "Tossing fresh vegetables high in the sizzling skillet for a golden sear...",
    color: "#f97316",
    tip: "💡 Tip: Give food space in the pan — space creates a crisp, delicious sear!",
    pct: 68,
  },
  {
    badge: "Stage 5: Simmering / Boiling",
    icon: "🍲",
    title: "Simmering Rich Broth",
    detail: "Melding herbs, spices, and broth with gentle steam and rising bubbles...",
    color: "#22c55e",
    tip: "💡 Tip: Low and slow simmering extracts deep savory richness and tenderness!",
    pct: 85,
  },
  {
    badge: "Stage 6: Serving / Plating",
    icon: "🍽️",
    title: "Ready to Serve!",
    detail: "Lifting the silver cloche platter with a completed, beautifully garnished dish...",
    color: "#a855f7",
    tip: "💡 Tip: Squeezing a drop of lemon right before serving instantly elevates dish aroma!",
    pct: 100,
  },
];

const CulinaryLoadingModal = ({ isOpen = true, onClose }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [progress, setProgress] = useState(16);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Lock scrolling on document body while modal is open
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    // Progress counter (advances smoothly toward 94% until recipe is ready)
    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev < 94) {
          const inc = Math.floor(Math.random() * 5) + 3;
          const next = Math.min(94, prev + inc);
          const targetIdx = Math.min(
            stages.length - 1,
            Math.floor((next / 94) * stages.length)
          );
          setCurrentIdx(targetIdx);
          return next;
        }
        return prev;
      });
    }, 450);

    // Only allow manual closing if generation takes > 1 minute
    setCanClose(false);
    const timeoutTimer = setTimeout(() => {
      setCanClose(true);
    }, 60000);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(timeoutTimer);
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const currentStage = stages[currentIdx];

  return (
    <div
      className="culinary-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="culinary-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Top Shimmer Gradient Accent */}
        <div className="culinary-shimmer-edge"></div>

        {/* Close button only shows after 1 minute if network delays occur */}
        {canClose && onClose && (
          <button
            type="button"
            className="culinary-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            title="Taking longer than expected. Click to close."
          >
            &times;
          </button>
        )}

        {/* ── 300px x 300px Crisp Vector SVG Stage ── */}
        <div className="culinary-svg-stage">
          {/* ── 1. CLEANING / WASHING SCENE ── */}
          <div className={`culinary-scene ${currentIdx === 0 ? "active" : ""}`}>
            <svg className="culinary-stage-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="115" fill="rgba(56, 189, 248, 0.12)" />
              <circle cx="150" cy="150" r="85" fill="none" stroke="rgba(56, 189, 248, 0.3)" strokeWidth="2" strokeDasharray="4 6" />

              <path d="M 80 40 L 150 40 L 150 75" fill="none" stroke="#94a3b8" strokeWidth="14" strokeLinecap="round" />
              <rect x="138" y="75" width="24" height="12" rx="4" fill="#64748b" />
              <circle cx="115" cy="30" r="8" fill="#38bdf8" />

              <line x1="150" y1="88" x2="150" y2="185" stroke="#38bdf8" strokeWidth="5" opacity="0.6" strokeDasharray="8 6" />

              <g style={{ animation: "faucetDrip 1.2s cubic-bezier(0.4, 0, 0.6, 1) infinite" }}>
                <circle cx="150" cy="100" r="7" fill="#38bdf8" />
                <path d="M 143 100 Q 150 86 157 100 Z" fill="#38bdf8" />
              </g>
              <g style={{ animation: "faucetDrip 1.2s cubic-bezier(0.4, 0, 0.6, 1) 0.5s infinite" }}>
                <circle cx="150" cy="100" r="6" fill="#38bdf8" />
                <path d="M 144 100 Q 150 88 156 100 Z" fill="#38bdf8" />
              </g>

              <g style={{ animation: "waterSplash 2s ease-in-out infinite" }}>
                <ellipse cx="150" cy="205" rx="72" ry="24" fill="#334155" />
                <path d="M 80 205 Q 150 250 220 205 Z" fill="#475569" />
                <text x="125" y="195" fontSize="44" textAnchor="middle">🥕</text>
                <text x="175" y="192" fontSize="46" textAnchor="middle">🥦</text>
                <text x="150" y="210" fontSize="38" textAnchor="middle">🍅</text>
              </g>

              <circle cx="120" cy="160" r="9" fill="rgba(56, 189, 248, 0.5)" stroke="#38bdf8" strokeWidth="1.5" style={{ animation: "bubbleFloat 1.8s ease-out infinite" }} />
              <circle cx="185" cy="170" r="7" fill="rgba(56, 189, 248, 0.5)" stroke="#38bdf8" strokeWidth="1.5" style={{ animation: "bubbleFloat 2.2s ease-out 0.7s infinite" }} />
              <circle cx="150" cy="150" r="11" fill="rgba(56, 189, 248, 0.4)" stroke="#38bdf8" strokeWidth="1.5" style={{ animation: "bubbleFloat 2s ease-out 1.2s infinite" }} />
            </svg>
          </div>

          {/* ── 2. CUTTING / CHOPPING SCENE ── */}
          <div className={`culinary-scene ${currentIdx === 1 ? "active" : ""}`}>
            <svg className="culinary-stage-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="115" fill="rgba(234, 179, 8, 0.12)" />

              <rect x="55" y="180" width="190" height="42" rx="10" fill="#78350f" stroke="#b45309" strokeWidth="3" />
              <rect x="65" y="186" width="170" height="6" rx="3" fill="#92400e" opacity="0.6" />

              <g style={{ animation: "sliceJitter 0.5s ease-in-out infinite" }}>
                <text x="105" y="175" fontSize="40" textAnchor="middle">🫑</text>
                <text x="150" y="175" fontSize="36" textAnchor="middle">🧅</text>
                <text x="195" y="175" fontSize="40" textAnchor="middle">🥕</text>
              </g>

              <g style={{ transformOrigin: "195px 165px", animation: "knifeChop 0.45s ease-in-out infinite" }}>
                <path d="M 95 145 Q 165 148 190 162 L 190 135 L 95 135 Z" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2" />
                <line x1="105" y1="140" x2="175" y2="140" stroke="white" strokeWidth="2" opacity="0.8" />
                <rect x="190" y="140" width="55" height="16" rx="5" fill="#1e293b" stroke="#475569" strokeWidth="2" />
                <circle cx="210" cy="148" r="2.5" fill="#e2e8f0" />
                <circle cx="230" cy="148" r="2.5" fill="#e2e8f0" />
              </g>

              <text x="145" y="140" fontSize="22" style={{ animation: "sparkleTwinkle 0.6s infinite" }}>✨</text>
            </svg>
          </div>

          {/* ── 3. FIRE / HEATING SCENE ── */}
          <div className={`culinary-scene ${currentIdx === 2 ? "active" : ""}`}>
            <svg className="culinary-stage-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="115" fill="rgba(239, 68, 68, 0.15)" />

              <ellipse cx="150" cy="225" rx="85" ry="18" fill="none" stroke="#475569" strokeWidth="5" />
              <line x1="150" y1="207" x2="150" y2="243" stroke="#475569" strokeWidth="4" />
              <line x1="75" y1="225" x2="225" y2="225" stroke="#475569" strokeWidth="4" />

              <g style={{ transformOrigin: "150px 220px", animation: "fireFlicker 0.4s ease-in-out infinite" }}>
                <path d="M 150 110 Q 185 170 170 215 Q 150 225 130 215 Q 115 170 150 110 Z" fill="#ef4444" opacity="0.9" />
                <path d="M 150 135 Q 175 180 162 215 Q 150 222 138 215 Q 125 180 150 135 Z" fill="#f97316" />
                <path d="M 150 165 Q 165 195 158 215 Q 150 220 142 215 Q 135 195 150 165 Z" fill="#fde047" />
              </g>
              <g style={{ transformOrigin: "110px 222px", animation: "fireFlicker 0.48s ease-in-out 0.15s infinite" }}>
                <path d="M 110 145 Q 130 185 124 218 Q 110 225 98 218 Q 90 185 110 145 Z" fill="#ef4444" opacity="0.8" />
                <path d="M 110 165 Q 124 195 118 218 Q 110 222 102 218 Q 96 195 110 165 Z" fill="#f97316" />
              </g>
              <g style={{ transformOrigin: "190px 222px", animation: "fireFlicker 0.44s ease-in-out 0.25s infinite" }}>
                <path d="M 190 145 Q 210 185 204 218 Q 190 225 178 218 Q 170 185 190 145 Z" fill="#ef4444" opacity="0.8" />
                <path d="M 190 165 Q 204 195 198 218 Q 190 222 182 218 Q 176 195 190 165 Z" fill="#f97316" />
              </g>

              <circle cx="130" cy="140" r="3.5" fill="#fde047" style={{ animation: "heatEmber 1.6s ease-out infinite" }} />
              <circle cx="170" cy="130" r="4.5" fill="#f97316" style={{ animation: "heatEmber 1.8s ease-out 0.6s infinite" }} />
              <circle cx="150" cy="115" r="3" fill="#fde047" style={{ animation: "heatEmber 1.4s ease-out 1s infinite" }} />
            </svg>
          </div>

          {/* ── 4. FRYING / TOSSING SCENE (100% Solid Veggies) ── */}
          <div className={`culinary-scene ${currentIdx === 3 ? "active" : ""}`}>
            <svg className="culinary-stage-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="115" fill="rgba(249, 115, 22, 0.12)" />

              <g style={{ transformOrigin: "150px 210px", animation: "wokToss 0.9s ease-in-out infinite" }}>
                <ellipse cx="140" cy="210" rx="75" ry="24" fill="#1e293b" stroke="#475569" strokeWidth="4" />
                <path d="M 68 210 Q 140 260 212 210 Z" fill="#334155" />
                <path d="M 212 210 Q 255 205 275 190" fill="none" stroke="#64748b" strokeWidth="9" strokeLinecap="round" />
                <path d="M 248 198 L 275 190" stroke="#78350f" strokeWidth="11" strokeLinecap="round" />
              </g>

              <circle cx="120" cy="215" r="3" fill="#fbbf24" />
              <circle cx="155" cy="218" r="2.5" fill="#fbbf24" />
              <circle cx="138" cy="222" r="3.5" fill="#fbbf24" />

              {/* High-flying 100% Solid Veggies */}
              <g style={{ animation: "flyingVeggie 1.3s cubic-bezier(0.25, 1, 0.5, 1) infinite" }}>
                <text x="120" y="200" fontSize="34" textAnchor="middle">🥦</text>
              </g>
              <g style={{ animation: "flyingVeggie 1.3s cubic-bezier(0.25, 1, 0.5, 1) 0.35s infinite" }}>
                <text x="155" y="200" fontSize="36" textAnchor="middle">🥕</text>
              </g>
              <g style={{ animation: "flyingVeggie 1.3s cubic-bezier(0.25, 1, 0.5, 1) 0.7s infinite" }}>
                <text x="140" y="200" fontSize="32" textAnchor="middle">🍄</text>
              </g>

              <text x="100" y="160" fontSize="20" style={{ animation: "sparkleTwinkle 1s infinite" }}>✨</text>
              <text x="190" y="150" fontSize="22" style={{ animation: "sparkleTwinkle 1.2s 0.5s infinite" }}>✨</text>
            </svg>
          </div>

          {/* ── 5. BOILING / SIMMERING SCENE ── */}
          <div className={`culinary-scene ${currentIdx === 4 ? "active" : ""}`}>
            <svg className="culinary-stage-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="115" fill="rgba(34, 197, 94, 0.12)" />

              <g style={{ animation: "steamWave 1.8s ease-out infinite" }}>
                <path d="M 130 115 Q 120 90 135 65 Q 150 40 130 20" fill="none" stroke="rgba(200, 240, 220, 0.7)" strokeWidth="4" strokeLinecap="round" />
              </g>
              <g style={{ animation: "steamWave 2.1s ease-out 0.6s infinite" }}>
                <path d="M 155 110 Q 170 85 155 60 Q 140 35 160 15" fill="none" stroke="rgba(200, 240, 220, 0.8)" strokeWidth="4" strokeLinecap="round" />
              </g>
              <g style={{ animation: "steamWave 1.9s ease-out 1.2s infinite" }}>
                <path d="M 175 118 Q 190 95 175 70 Q 160 45 180 25" fill="none" stroke="rgba(200, 240, 220, 0.6)" strokeWidth="3.5" strokeLinecap="round" />
              </g>

              <g style={{ animation: "potSimmer 0.6s ease-in-out infinite" }}>
                <path d="M 60 160 Q 45 160 45 175 Q 45 190 60 190" fill="none" stroke="#64748b" strokeWidth="6" strokeLinecap="round" />
                <path d="M 240 160 Q 255 160 255 175 Q 255 190 240 190" fill="none" stroke="#64748b" strokeWidth="6" strokeLinecap="round" />

                <rect x="60" y="150" width="180" height="90" rx="14" fill="#334155" stroke="#475569" strokeWidth="4" />
                <ellipse cx="150" cy="150" rx="90" ry="20" fill="#22c55e" opacity="0.4" />
                <ellipse cx="150" cy="152" rx="80" ry="16" fill="#15803d" />

                <circle cx="125" cy="152" r="7" fill="#86efac" opacity="0.8" />
                <circle cx="165" cy="154" r="9" fill="#86efac" opacity="0.9" />
                <circle cx="145" cy="150" r="5" fill="#86efac" opacity="0.7" />
                <circle cx="185" cy="152" r="6" fill="#86efac" opacity="0.8" />
              </g>
            </svg>
          </div>

          {/* ── 6. SERVING / PLATING SCENE ── */}
          <div className={`culinary-scene ${currentIdx === 5 ? "active" : ""}`}>
            <svg className="culinary-stage-svg" viewBox="0 0 300 300">
              <circle cx="150" cy="150" r="115" fill="rgba(168, 85, 247, 0.15)" />

              <ellipse cx="150" cy="220" rx="100" ry="22" fill="#64748b" stroke="#cbd5e1" strokeWidth="4" />
              <ellipse cx="150" cy="216" rx="85" ry="16" fill="#94a3b8" />

              <text x="150" y="210" fontSize="52" textAnchor="middle">🥗</text>

              <g style={{ animation: "clocheLift 2.2s ease-in-out infinite" }}>
                <path d="M 75 160 Q 150 80 225 160 Z" fill="#94a3b8" stroke="#cbd5e1" strokeWidth="3.5" />
                <circle cx="150" cy="80" r="9" fill="#e2e8f0" stroke="#64748b" strokeWidth="2" />
                <path d="M 100 150 Q 150 98 190 135" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.7" />
              </g>

              <text x="75" y="110" fontSize="28" style={{ animation: "sparkleTwinkle 1.4s infinite" }}>✨</text>
              <text x="220" y="100" fontSize="32" style={{ animation: "sparkleTwinkle 1.8s 0.5s infinite" }}>🌟</text>
              <text x="210" y="200" fontSize="26" style={{ animation: "sparkleTwinkle 1.6s 0.9s infinite" }}>✨</text>
              <text x="80" y="210" fontSize="24" style={{ animation: "sparkleTwinkle 1.5s 0.3s infinite" }}>🌟</text>
            </svg>
          </div>
        </div>

        {/* ── Active Stage Badge ── */}
        <div>
          <span
            className="culinary-workflow-badge"
            style={{
              color: currentStage.color,
              borderColor: `${currentStage.color}55`,
              backgroundColor: `${currentStage.color}1f`,
            }}
          >
            <span>{currentStage.icon}</span>
            <span>{currentStage.badge}</span>
          </span>
        </div>

        <h3 className="culinary-modal-title">{currentStage.title}</h3>
        <p className="culinary-modal-subtitle">{currentStage.detail}</p>

        {/* ── 6-Step Workflow Indicator Strip ── */}
        <div className="culinary-steps-strip">
          {stages.map((stage, idx) => {
            const isActive = idx === currentIdx;
            const isPassed = idx < currentIdx;
            return (
              <div
                key={stage.badge}
                className={`culinary-step-dot ${isActive ? "active" : ""} ${isPassed ? "passed" : ""}`}
                style={
                  isActive
                    ? {
                        backgroundColor: stage.color,
                        boxShadow: `0 0 10px ${stage.color}`,
                      }
                    : {}
                }
                title={stage.badge}
              />
            );
          })}
        </div>

        {/* ── Progress Bar ── */}
        <div className="culinary-progress-wrap">
          <div className="culinary-progress-meta">
            <span>Curating Recipe...</span>
            <span className="culinary-progress-pct" style={{ color: currentStage.color }}>
              {progress}%
            </span>
          </div>
          <div className="culinary-progress-track">
            <div
              className="culinary-progress-fill"
              style={{
                width: `${progress}%`,
                background: currentStage.color,
                boxShadow: `0 0 10px ${currentStage.color}88`,
              }}
            ></div>
          </div>
        </div>

        {/* ── Chef Tip Card ── */}
        <div className="culinary-chef-tip-box">
          <span className="culinary-tip-icon">👨‍🍳</span>
          <div className="culinary-tip-text">{currentStage.tip}</div>
        </div>
      </div>
    </div>
  );
};

export default CulinaryLoadingModal;
